import { formatCurrency, formatDate } from '@if/core/utils'
import { Card, CardContent, CardHeader, Divider, Grid, Stack, Typography } from '@mui/material'
import { EasyCareWorksheet } from '@src/data/types/EasyCareWorksheetSchema'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const formatAnnualRate = (fraction: number) => `${(fraction * 100).toFixed(2)} %`

interface DetailRowProps {
  label: string
  value: ReactNode
  strong?: boolean
}

const DetailRow = ({ label, value, strong = false }: DetailRowProps) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography color={strong ? 'text.primary' : 'text.secondary'} fontWeight={strong ? 500 : undefined}>
      {label}
    </Typography>
    <Typography fontWeight={strong ? 500 : undefined} sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Stack>
)

const DetailCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card variant="outlined" component="section" aria-label={title} sx={{ height: '100%' }}>
    <CardHeader
      title={title}
      slotProps={{ title: { variant: 'subtitle1', component: 'h3', fontWeight: 500 } }}
      sx={{ pb: 0 }}
    />
    <CardContent>
      <Stack spacing={0.5}>{children}</Stack>
    </CardContent>
  </Card>
)

interface Props {
  worksheet: EasyCareWorksheet
}

const EasyCareDetailCards = ({ worksheet }: Props) => {
  const { t } = useTranslation()
  const notIncluded = <Typography color="text.secondary">{t('easyCare.notIncluded')}</Typography>

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <DetailCard title={t('easyCare.vehicleAssistance')}>
          {worksheet.includeVehicleAssistance ? (
            <>
              <DetailRow
                label={t('easyCare.coverage')}
                value={
                  worksheet.vehicleAssistanceTerm
                    ? t('easyCare.months', { count: worksheet.vehicleAssistanceTerm })
                    : '—'
                }
              />
              <DetailRow label={t('easyCare.price')} value={formatCurrency(worksheet.vehicleAssistanceAmount)} />
              <DetailRow label={t('worksheet.tax')} value={formatCurrency(worksheet.vehicleAssistanceTaxes)} />
              <Divider sx={{ my: 1 }} />
              <DetailRow
                strong
                label={t('easyCare.subtotal')}
                value={formatCurrency(worksheet.vehicleAssistanceAmount + worksheet.vehicleAssistanceTaxes)}
              />
            </>
          ) : (
            notIncluded
          )}
        </DetailCard>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <DetailCard title={t('easyCare.loanProtection')}>
          {worksheet.includeLoanProtection ? (
            <>
              <DetailRow label={t('easyCare.parentLoanBalance')} value={formatCurrency(worksheet.parentLoanBalance)} />
              <DetailRow
                label={t('easyCare.rateAndCoverage')}
                value={`${formatAnnualRate(worksheet.loanProtectionAnnualRate)} · ${t('easyCare.months', { count: worksheet.loanProtectionTerm })}`}
              />
              <Divider sx={{ my: 1 }} />
              <DetailRow strong label={t('easyCare.premium')} value={formatCurrency(worksheet.loanProtectionAmount)} />
            </>
          ) : (
            notIncluded
          )}
        </DetailCard>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <DetailCard title={t('easyCare.schedule')}>
          <DetailRow label={t('worksheetCommon.activationDate')} value={formatDate(worksheet.deliveryOn)} />
          <DetailRow label={t('worksheet.firstPaymentOn')} value={formatDate(worksheet.firstPaymentOn)} />
          <DetailRow
            label={t('worksheet.paymentFrequency')}
            value={t(`EPaymentFrequency.${worksheet.paymentFrequency}`, { ns: 'enum' })}
          />
        </DetailCard>
      </Grid>
    </Grid>
  )
}

export default EasyCareDetailCards
